import React, { useEffect, useState } from "react";
import { Drawer, Spin, Alert, Tag, Table, Button, message, Popconfirm, Radio, DatePicker, InputNumber } from "antd";
import { CheckCircleOutlined, GiftOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  PlatformAnalyticsCreatorDetail,
  PlatformAnalyticsSaasPlanGrantTrial,
} from "../../utils/ApiEndpoints";
import customClientAxios from "../../customAxios";
import { fmtRupees, fmtNum } from "../paUtils";
import styled from "styled-components";

const PLAN_META = {
  free:     { label: "Free",     color: "#94a3b8" },
  growth:   { label: "Growth",   color: "#10b981" },
  pro:      { label: "Pro",      color: "#6366f1" },
  advanced: { label: "Advanced", color: "#a855f7" },
};

const fmtDate = (v) =>
  v ? new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const Section = styled.div`
  margin-bottom: 20px;
`;

const SectionHeading = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
  padding-bottom: 6px;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StatRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f8fafc;
  font-size: 13px;
`;

const StatKey = styled.span`color: #64748b;`;
const StatVal = styled.span`font-weight: 600; color: #1e293b;`;

const KYC_COLORS = {
  verified: "green",
  submitted: "blue",
  partial: "orange",
  pending: "gold",
  rejected: "red",
};

function CreatorDetailDrawer({ brandPageId, open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [trialPlan, setTrialPlan] = useState("pro");
  const [trialStartDate, setTrialStartDate] = useState(() => dayjs().startOf("day"));
  const [paidAmount, setPaidAmount] = useState(null);     // ₹ entered for paid advanced
  const [paidMonths, setPaidMonths] = useState(12);       // default 12 months for paid
  const [granting, setGranting] = useState(false);

  // Advanced is "contact sales" — never a trial, always a paid manual grant.
  const isPaidMode = trialPlan === "advanced";

  const fetchData = () => {
    if (!open || !brandPageId) return;
    setLoading(true);
    setError(null);
    customClientAxios()
      .get(`${PlatformAnalyticsCreatorDetail}/${brandPageId}`)
      .then((res) => setData(res.data.data))
      .catch((e) => setError(e?.response?.data?.error || "Failed to load creator details"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, [open, brandPageId]);

  const handleGrantTrial = async () => {
    if (!brandPageId || !trialStartDate) return;
    if (isPaidMode && (!paidAmount || paidAmount <= 0)) {
      message.error("Amount is required for paid Advanced grant");
      return;
    }
    setGranting(true);
    try {
      const body = {
        brandPageId,
        plan: trialPlan,
        startDate: trialStartDate.toISOString(),
        mode: isPaidMode ? "paid" : "trial",
      };
      if (isPaidMode) {
        body.amount = paidAmount;          // rupees
        body.periodMonths = paidMonths;
      }
      const res = await customClientAxios().post(
        PlatformAnalyticsSaasPlanGrantTrial,
        body
      );
      const d = res.data?.data;
      if (isPaidMode) {
        message.success(
          `Marked ${trialPlan} as paid (₹${paidAmount.toLocaleString("en-IN")}) until ${fmtDate(d?.periodEnd)}`
        );
      } else {
        message.success(`Granted ${trialPlan} trial until ${fmtDate(d?.trialExpiresAt)}`);
      }
      fetchData();
    } catch (e) {
      message.error(e?.response?.data?.error || e?.response?.data?.message || "Failed to grant plan");
    } finally {
      setGranting(false);
    }
  };

  const trialEndDate = trialStartDate
    ? trialStartDate.add(isPaidMode ? paidMonths : 1, "month")
    : null;

  const handleVerifyKyc = async () => {
    setVerifying(true);
    try {
      await customClientAxios().patch(
        `${PlatformAnalyticsCreatorDetail}/${brandPageId}/kyc-status`,
        { kycStatus: "verified" }
      );
      message.success("KYC status updated to verified");
      fetchData();
    } catch (e) {
      message.error(e?.response?.data?.error || "Failed to update KYC status");
    } finally {
      setVerifying(false);
    }
  };

  const offeringsColumns = [
    { title: "Title", dataIndex: "title", key: "title", ellipsis: true },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (v) => (
        <Tag color={v === "published" ? "green" : "orange"}>{v}</Tag>
      ),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (v) => v ? new Date(v).toLocaleDateString("en-IN") : "-",
    },
  ];

  const kycStatus = data?.brandPage?.kycStatus;
  const bank = data?.bankDetails;
  const tax = data?.taxDetails;

  return (
    <Drawer
      title={data?.brandPage?.name || "Creator Detail"}
      placement="right"
      width={520}
      open={open}
      onClose={onClose}
      destroyOnClose
    >
      {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}
      <Spin spinning={loading}>
        {data && (
          <>
            <Section>
              <SectionHeading>Brand Page</SectionHeading>
              <StatRow><StatKey>Name</StatKey><StatVal>{data.brandPage?.name}</StatVal></StatRow>
              <StatRow><StatKey>Slug</StatKey><StatVal>{data.brandPage?.slug}</StatVal></StatRow>
              <StatRow>
                <StatKey>Status</StatKey>
                <StatVal>
                  <Tag color={data.brandPage?.status === "active" ? "green" : "orange"}>
                    {data.brandPage?.status}
                  </Tag>
                </StatVal>
              </StatRow>
              <StatRow>
                <StatKey>KYC</StatKey>
                <StatVal style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Tag color={KYC_COLORS[kycStatus] || "default"}>
                    {kycStatus}
                  </Tag>
                  {kycStatus !== "verified" && (
                    <Popconfirm
                      title="Verify KYC"
                      description="Are you sure you want to mark this KYC as verified?"
                      onConfirm={handleVerifyKyc}
                      okText="Yes, Verify"
                      cancelText="Cancel"
                    >
                      <Button
                        type="primary"
                        size="small"
                        icon={<CheckCircleOutlined />}
                        loading={verifying}
                        style={{ background: "#10b981", borderColor: "#10b981" }}
                      >
                        Verify
                      </Button>
                    </Popconfirm>
                  )}
                </StatVal>
              </StatRow>
              <StatRow>
                <StatKey>Created</StatKey>
                <StatVal>{data.brandPage?.createdAt ? new Date(data.brandPage.createdAt).toLocaleDateString("en-IN") : "-"}</StatVal>
              </StatRow>
            </Section>

            <Section>
              <SectionHeading>SaaS Plan</SectionHeading>
              <StatRow>
                <StatKey>Current Plan</StatKey>
                <StatVal>
                  <Tag color={PLAN_META[data.plan?.current || "free"]?.color}>
                    {PLAN_META[data.plan?.current || "free"]?.label || data.plan?.current}
                  </Tag>
                  {data.plan?.trialExpiresAt && (
                    <Tag color="gold" style={{ marginLeft: 6 }}>trial</Tag>
                  )}
                </StatVal>
              </StatRow>
              {data.plan?.subscriptionStatus && (
                <StatRow>
                  <StatKey>Razorpay Sub Status</StatKey>
                  <StatVal>{data.plan.subscriptionStatus}</StatVal>
                </StatRow>
              )}
              <StatRow>
                <StatKey>Plan Start</StatKey>
                <StatVal>
                  {fmtDate(
                    data.plan?.periodStart ||
                      (data.plan?.trialExpiresAt
                        ? new Date(new Date(data.plan.trialExpiresAt).setMonth(
                            new Date(data.plan.trialExpiresAt).getMonth() - 1
                          )).toISOString()
                        : null)
                  )}
                </StatVal>
              </StatRow>
              <StatRow>
                <StatKey>Plan End</StatKey>
                <StatVal>{fmtDate(data.plan?.periodEnd || data.plan?.trialExpiresAt)}</StatVal>
              </StatRow>
              <StatRow>
                <StatKey>Plan Revenue</StatKey>
                <StatVal>
                  {fmtRupees(data.plan?.revenue || 0)}
                  {data.plan?.txnCount > 0 && (
                    <span style={{ color: "#94a3b8", fontWeight: 400, marginLeft: 6 }}>
                      ({data.plan.txnCount} txn{data.plan.txnCount > 1 ? "s" : ""})
                    </span>
                  )}
                </StatVal>
              </StatRow>
              <div style={{ marginTop: 12, padding: 12, background: "#f8fafc", borderRadius: 8 }}>
                <div style={{ fontSize: 12, color: "#475569", marginBottom: 8, fontWeight: 600 }}>
                  {isPaidMode ? "Mark as paid (Advanced — contact sales)" : "Grant 1-month free trial"}
                </div>

                <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>Plan</div>
                <Radio.Group
                  value={trialPlan}
                  onChange={(e) => setTrialPlan(e.target.value)}
                  size="small"
                  style={{ marginBottom: 10 }}
                >
                  <Radio.Button value="growth">Growth</Radio.Button>
                  <Radio.Button value="pro">Pro</Radio.Button>
                  <Radio.Button value="advanced">Advanced</Radio.Button>
                </Radio.Group>

                <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>Start date</div>
                <DatePicker
                  value={trialStartDate}
                  onChange={(d) => setTrialStartDate(d)}
                  allowClear={false}
                  size="small"
                  format="DD MMM YYYY"
                  style={{ width: 180 }}
                />

                {isPaidMode && (
                  <div style={{ marginTop: 10, display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
                    <div>
                      <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>
                        Amount (₹) <span style={{ color: "#dc2626" }}>*</span>
                      </div>
                      <InputNumber
                        size="small"
                        min={1}
                        value={paidAmount}
                        onChange={setPaidAmount}
                        placeholder="e.g. 25000"
                        style={{ width: 140 }}
                        formatter={(v) => (v ? `₹ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "")}
                        parser={(v) => (v ? v.replace(/[^\d]/g, "") : "")}
                      />
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>Period (months)</div>
                      <InputNumber
                        size="small"
                        min={1}
                        max={60}
                        value={paidMonths}
                        onChange={(v) => setPaidMonths(v || 12)}
                        style={{ width: 100 }}
                      />
                    </div>
                  </div>
                )}

                {trialEndDate && (
                  <div style={{ marginTop: 8, marginBottom: 10, fontSize: 11, color: "#64748b" }}>
                    Ends: <strong style={{ color: "#0f172a" }}>{trialEndDate.format("DD MMM YYYY")}</strong>{" "}
                    <span style={{ color: "#94a3b8" }}>
                      (start + {isPaidMode ? `${paidMonths} months` : "1 month"})
                    </span>
                  </div>
                )}

                <div>
                  <Popconfirm
                    title={isPaidMode ? `Mark ${trialPlan} as paid?` : `Grant ${trialPlan} trial?`}
                    description={
                      <div style={{ maxWidth: 320 }}>
                        {isPaidMode ? (
                          <>
                            Records a PAID {paidMonths}-month Advanced subscription
                            for <strong>₹{(paidAmount || 0).toLocaleString("en-IN")}</strong> starting{" "}
                            <strong>{trialStartDate?.format("DD MMM YYYY") || "—"}</strong>. Counts as plan revenue.
                          </>
                        ) : (
                          <>
                            Inserts a 1-month trial SubscriptionTransaction starting{" "}
                            <strong>{trialStartDate?.format("DD MMM YYYY") || "—"}</strong>.
                            Refuses if currently on a paid plan.
                          </>
                        )}
                      </div>
                    }
                    onConfirm={handleGrantTrial}
                    okText={isPaidMode ? "Yes, mark paid" : "Yes, grant"}
                    cancelText="Cancel"
                  >
                    <Button
                      type="primary"
                      size="small"
                      icon={<GiftOutlined />}
                      loading={granting}
                      disabled={!trialStartDate || (isPaidMode && (!paidAmount || paidAmount <= 0))}
                    >
                      {isPaidMode ? "Mark as Paid" : "Grant Trial"}
                    </Button>
                  </Popconfirm>
                </div>
              </div>
            </Section>

            <Section>
              <SectionHeading>Plan Transaction History</SectionHeading>
              {(data.subscriptionHistory || []).length === 0 ? (
                <div style={{ color: "#94a3b8", fontSize: 13, padding: "8px 0" }}>
                  No subscription transactions yet
                </div>
              ) : (
                <Table
                  size="small"
                  rowKey="id"
                  dataSource={data.subscriptionHistory}
                  pagination={false}
                  columns={[
                    {
                      title: "Type",
                      dataIndex: "source",
                      key: "source",
                      width: 70,
                      render: (s) =>
                        s === "trial" ? (
                          <Tag color="purple" style={{ margin: 0 }}>Trial</Tag>
                        ) : (
                          <Tag color="green" style={{ margin: 0 }}>Paid</Tag>
                        ),
                    },
                    {
                      title: "Plan",
                      dataIndex: "plan",
                      key: "plan",
                      width: 90,
                      render: (p) => (
                        <Tag color={PLAN_META[p]?.color || "default"} style={{ margin: 0 }}>
                          {PLAN_META[p]?.label || p}
                        </Tag>
                      ),
                    },
                    {
                      title: "Amount",
                      dataIndex: "amount",
                      key: "amount",
                      width: 100,
                      align: "right",
                      render: (a) =>
                        a > 0 ? (
                          <span style={{ fontWeight: 600 }}>{fmtRupees(a)}</span>
                        ) : (
                          <span style={{ color: "#94a3b8" }}>—</span>
                        ),
                    },
                    {
                      title: "Period",
                      key: "period",
                      render: (_, r) =>
                        r.periodStart && r.periodEnd ? (
                          <span style={{ fontSize: 12 }}>
                            {fmtDate(r.periodStart)} → {fmtDate(r.periodEnd)}
                          </span>
                        ) : (
                          <span style={{ color: "#94a3b8" }}>—</span>
                        ),
                    },
                    {
                      title: "Recorded",
                      dataIndex: "createdAt",
                      key: "createdAt",
                      width: 110,
                      render: (d) => (
                        <span style={{ fontSize: 12, color: "#64748b" }}>{fmtDate(d)}</span>
                      ),
                    },
                  ]}
                />
              )}
            </Section>

            <Section>
              <SectionHeading>Bank Account Details</SectionHeading>
              {bank?.accountNumber ? (
                <>
                  <StatRow><StatKey>Account Holder</StatKey><StatVal>{bank.accountHolderName || "—"}</StatVal></StatRow>
                  <StatRow><StatKey>Account Number</StatKey><StatVal>{bank.accountNumber}</StatVal></StatRow>
                  <StatRow><StatKey>IFSC</StatKey><StatVal>{bank.ifsc || "—"}</StatVal></StatRow>
                  <StatRow><StatKey>Bank Name</StatKey><StatVal>{bank.bankName || "—"}</StatVal></StatRow>
                  <StatRow>
                    <StatKey>Bank Verified</StatKey>
                    <StatVal>
                      <Tag color={bank.isVerified ? "green" : "gold"}>
                        {bank.isVerified ? "Verified" : "Not Verified"}
                      </Tag>
                    </StatVal>
                  </StatRow>
                </>
              ) : (
                <div style={{ color: "#94a3b8", fontSize: 13, padding: "8px 0" }}>No bank details submitted</div>
              )}
            </Section>

            {(tax?.panNumber || tax?.gstNumber) && (
              <Section>
                <SectionHeading>Tax Details</SectionHeading>
                {tax.legalBusinessName && <StatRow><StatKey>Business Name</StatKey><StatVal>{tax.legalBusinessName}</StatVal></StatRow>}
                {tax.businessType && <StatRow><StatKey>Business Type</StatKey><StatVal>{tax.businessType}</StatVal></StatRow>}
                {tax.panNumber && <StatRow><StatKey>PAN</StatKey><StatVal>{tax.panNumber}</StatVal></StatRow>}
                {tax.gstNumber && <StatRow><StatKey>GST</StatKey><StatVal>{tax.gstNumber}</StatVal></StatRow>}
              </Section>
            )}

            <Section>
              <SectionHeading>Owner</SectionHeading>
              <StatRow><StatKey>Name</StatKey><StatVal>{data.owner?.name}</StatVal></StatRow>
              <StatRow><StatKey>Email</StatKey><StatVal>{data.owner?.email}</StatVal></StatRow>
              <StatRow><StatKey>Phone</StatKey><StatVal>{data.owner?.phone}</StatVal></StatRow>
              <StatRow>
                <StatKey>Signup Date</StatKey>
                <StatVal>{data.owner?.signupDate ? new Date(data.owner.signupDate).toLocaleDateString("en-IN") : "-"}</StatVal>
              </StatRow>
              <StatRow>
                <StatKey>Last Login</StatKey>
                <StatVal>{data.owner?.lastLogin ? new Date(data.owner.lastLogin).toLocaleDateString("en-IN") : "-"}</StatVal>
              </StatRow>
            </Section>

            <Section>
              <SectionHeading>Revenue</SectionHeading>
              <StatRow><StatKey>GMV</StatKey><StatVal>{fmtRupees(data.revenue?.gmv)}</StatVal></StatRow>
              <StatRow><StatKey>Platform Fee</StatKey><StatVal>{fmtRupees(data.revenue?.platformFee)}</StatVal></StatRow>
              <StatRow><StatKey>Convenience Fee</StatKey><StatVal>{fmtRupees(data.revenue?.convenienceFee)}</StatVal></StatRow>
              <StatRow><StatKey>Creator Payout</StatKey><StatVal>{fmtRupees(data.revenue?.creatorPayout)}</StatVal></StatRow>
              <StatRow><StatKey>Transactions</StatKey><StatVal>{fmtNum(data.revenue?.transactionCount)}</StatVal></StatRow>
              <StatRow><StatKey>Unique Customers</StatKey><StatVal>{fmtNum(data.revenue?.uniqueCustomers)}</StatVal></StatRow>
            </Section>

            <Section>
              <SectionHeading>WhatsApp Credits</SectionHeading>
              <StatRow><StatKey>Remaining</StatKey><StatVal>{fmtNum(data.whatsappCredits?.remaining)}</StatVal></StatRow>
              <StatRow><StatKey>Total Purchased</StatKey><StatVal>{fmtNum(data.whatsappCredits?.totalPurchased)}</StatVal></StatRow>
              <StatRow><StatKey>Total Used</StatKey><StatVal>{fmtNum(data.whatsappCredits?.totalUsed)}</StatVal></StatRow>
            </Section>

            <Section>
              <SectionHeading>Settlements</SectionHeading>
              {Object.entries(data.settlements || {}).map(([status, info]) => (
                <StatRow key={status}>
                  <StatKey>{status} ({info.count})</StatKey>
                  <StatVal>{fmtRupees(info.amount)}</StatVal>
                </StatRow>
              ))}
            </Section>

            <Section>
              <SectionHeading>Offerings ({(data.offerings || []).length})</SectionHeading>
              <Table
                dataSource={data.offerings || []}
                columns={offeringsColumns}
                rowKey="featureItemId"
                size="small"
                pagination={false}
                scroll={{ y: 200 }}
              />
            </Section>
          </>
        )}
      </Spin>
    </Drawer>
  );
}

export default CreatorDetailDrawer;
